import { numFormat } from 'src/localization/Num'
import {
  ConfigResponse,
  LocalizedConfigOption,
  TeamSize,
} from 'src/models/config.models'

/**
 * Pure derivation helpers over the session `/config` response, ported
 * verbatim from the deleted reselect combiners in
 * `src/redux/selectors/data/config.selectors.ts`. Consumers use them as
 * `useQuery` select fns / `useMemo` derivations over
 * `sessionConfigQueries.byLocale(locale)` data, defaulting to `[]` while
 * the query is pending (the legacy config reducer had no loading flags and
 * rendered empty option lists until the fetch succeeded).
 */

const localizedConfigOptionToDetails = (option: LocalizedConfigOption) => {
  const [value, text] = Object.entries(option)[0] as [string, string]

  return {
    value,
    details: {
      text,
    },
  }
}

export const industryOptionsFromConfig = (config: ConfigResponse) => {
  const mappedIndustries = config.supportedIndustriesLocalized.map(
    localizedConfigOptionToDetails,
  )

  const other = mappedIndustries.filter(
    (industry) => industry?.value === 'Other',
  )[0]

  const sorted = mappedIndustries
    .sort((a, b) =>
      a.details.text > b.details.text
        ? 1
        : b.details.text > a.details.text
          ? -1
          : 0,
    )
    .filter((industry) => industry?.value !== 'Other')

  if (other) sorted.push(other)

  return sorted
}

export const departmentOptionsFromConfig = (config: ConfigResponse) => {
  const mappedDepartments = config.departmentOptionsLocalized.map(
    localizedConfigOptionToDetails,
  )

  const other = mappedDepartments.filter(
    (department) => department?.value === 'Other',
  )[0]

  const sorted = mappedDepartments
    .sort((a, b) =>
      a.details.text > b.details.text
        ? 1
        : b.details.text > a.details.text
          ? -1
          : 0,
    )
    .filter((department) => department?.value !== 'Other')

  if (other) sorted.push(other)

  return sorted
}

export const teamSizeOptionsFromConfig = (
  config: ConfigResponse,
  locale: string,
) => {
  const teamSizeText = (teamSize: TeamSize): string =>
    teamSize.max
      ? `${numFormat(locale, teamSize.min)} - ${numFormat(locale, teamSize.max)}`
      : `${numFormat(locale, teamSize.min)}+`

  return config.teamSizeOptions.map((teamSize) => {
    return {
      value: teamSizeText(teamSize),
      details: {
        text: teamSizeText(teamSize),
        min: teamSize.min,
        max: teamSize.max,
      },
    }
  })
}

export const referralSourceOptionsFromConfig = (config: ConfigResponse) => {
  const localizedOptions = config.referralSourceOptionsLocalized

  return localizedOptions
    ? localizedOptions.map(localizedConfigOptionToDetails)
    : []
}
